import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { CompanyService } from '@app/features/companies/company.service';
import { SchedulePart } from '@app/features/contracts/contract.model';
import { DayOfWeekLabelPipe } from '@app/features/horaires/day-of-week-label-pipe';
import {
  maxTotalHours,
  minTimeBetweenDay,
  noOverlapValidator,
} from '@app/features/horaires/schedule.validators';
import {
  createScheduleFormGroupOfDay,
  DayOfWeek,
  DAYS_OF_WEEK,
  Schedule,
} from '@app/features/horaires/schedules.model';
import { SlotsDurationPipe } from '@app/features/horaires/slots-duration-pipe';
import { slotsLabel } from '@app/features/horaires/slots-label';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { SlotForm } from '@app/shared/forms/slot-form/slot-form';
import { createSlotFormGroup, Slot } from '@app/shared/forms/slot-form/slot.model';
import { endAfterStartValidator } from '@app/shared/forms/slot-form/slot.validators';
import { map, startWith } from 'rxjs';

export interface SchedulesFormValue {
  collectiveSchedules: boolean | null;
  schedules: Schedule[];
}

@Component({
  selector: 'app-schedules-form',
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    SlotForm,
    SlotsDurationPipe,
    MatTooltip,
    DayOfWeekLabelPipe,
  ],
  templateUrl: './schedules-form.html',
  styleUrls: ['./schedules-form.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
// TODO Handle collective case (one slot with all days having the same schedule)
export class SchedulesForm implements OnInit, OnChanges {
  @Input() initWith?: SchedulesFormValue;
  companyId = input<string | undefined>(undefined);
  validation = input<boolean>(false);
  readOnly = input<boolean>(false);
  /**
   * Les horaires d'avant un avenant (RG-avenant-consultation) : un créneau ou une journée qui en
   * diffère est surligné, l'ancienne valeur en info-bulle.
   */
  previous = input<SchedulesFormValue | null>(null);

  private cdr = inject(ChangeDetectorRef);
  private companyService = inject(CompanyService);

  private companySchedules: Schedule[] | undefined = undefined;
  private initialValues?: SchedulesFormValue;
  daysOfWeek = DAYS_OF_WEEK;
  form = new FormGroup({
    collectiveSchedules: new FormControl(false),
    schedules: new FormArray(
      this.daysOfWeek.map(
        (day) =>
          new FormGroup(
            {
              day: new FormControl<DayOfWeek>(day, { nonNullable: true }),
              slots: new FormArray([
                new FormGroup<ControlsOf<Slot>>(
                  {
                    startTime: new FormControl('', { nonNullable: true }),
                    endTime: new FormControl('', { nonNullable: true }),
                  },
                  { validators: [endAfterStartValidator()] },
                ),
              ]),
            },
            { validators: [noOverlapValidator()] },
          ),
      ),
      { validators: [minTimeBetweenDay(11), maxTotalHours(35), Validators.required] },
    ),
  });

  readonly valueSig = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map(() => this.form.getRawValue() as SchedulesFormValue),
    ),
    {
      initialValue: this.form.getRawValue() as SchedulesFormValue,
    },
  );
  collectiveSchedulesMatTooltip: string | null = null;
  slotsMatTooltip: string | null = null;

  constructor() {
    effect(() => this.valueSig());
    effect(() => {
      this.applyReadOnlyState(this.readOnly());
    });
    effect(() => {
      if (this.validation()) {
        this.form.markAllAsTouched();
      }
    });
  }

  /** L'info-bulle d'un créneau qui diffère de celui d'avant l'avenant ; null s'il ne change pas. */
  slotChange(dayIndex: number, slotIndex: number): string | null {
    const before = this.previousSlots(dayIndex);
    if (before === null) {
      return null;
    }
    const slot = this.form.controls.schedules.at(dayIndex).controls.slots.at(slotIndex);
    const current = slotsLabel(slot ? [slot.getRawValue() as Slot] : []);
    const previous = slotsLabel(before[slotIndex] ? [before[slotIndex]] : []);
    return current === previous ? null : `Avant l'avenant : ${previous}`;
  }

  /** L'info-bulle d'une journée dont les créneaux diffèrent d'avant l'avenant. */
  dayChange(dayIndex: number): string | null {
    const before = this.previousSlots(dayIndex);
    if (before === null) {
      return null;
    }
    const current = slotsLabel(
      this.form.controls.schedules.at(dayIndex).controls.slots.getRawValue() as Slot[],
    );
    const previous = slotsLabel(before);
    return current === previous ? null : `Avant l'avenant : ${previous}`;
  }

  private previousSlots(dayIndex: number): Slot[] | null {
    const previous = this.previous();
    if (!previous) {
      return null;
    }
    const day = this.form.controls.schedules.at(dayIndex)?.controls.day.value;
    return previous.schedules.find((schedule) => schedule.day === day)?.slots ?? [];
  }

  ngOnInit(): void {
    if (this.initWith) {
      this.initialValues = structuredClone(this.initWith);

      this.form.controls.collectiveSchedules.setValue(this.initWith.collectiveSchedules);

      const schedulesArray = this.form.controls.schedules;
      this.initialValues.schedules.forEach((dayValue, index) => {
        const slots = schedulesArray.at(index).controls.slots;
        slots.clear();

        dayValue.slots.forEach((slot) => {
          slots.push(this.createSlotGroup(slot));
        });
      });

      this.form.patchValue(this.initialValues);
    }
    if (!this.companyId()) {
      this.form.controls.collectiveSchedules.disable();
      this.collectiveSchedulesMatTooltip =
        'Veuillez sélectionner une entreprise pour activer cette option.';
    }
    this.updateCompanySchedules();
  }

  createSlotGroup(slot?: Slot) {
    return new FormGroup<ControlsOf<Slot>>(
      {
        startTime: new FormControl(slot?.startTime ?? '', { nonNullable: true }),
        endTime: new FormControl(slot?.endTime ?? '', { nonNullable: true }),
      },
      { validators: [endAfterStartValidator()] },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId']) {
      this.updateCompanySchedules();
    }
  }

  updateCompanySchedules() {
    if (this.companyId()) {
      if (!this.readOnly()) {
        this.form.controls.collectiveSchedules.enable({ emitEvent: false });
      }
      this.collectiveSchedulesMatTooltip = null;
      this.companyService.allCompanies$.subscribe((companies) => {
        const company = companies.find((c) => c.id === this.companyId());
        this.companySchedules = company ? company.collectiveSchedules : undefined;
        if (!this.companySchedules || this.companySchedules.length === 0) {
          this.form.controls.collectiveSchedules.setValue(false);
          this.form.controls.collectiveSchedules.disable({ emitEvent: false });
          this.collectiveSchedulesMatTooltip =
            "Cette entreprise n'a pas d'horaires collectifs définis. Veuillez en définir pour activer cette option.";
          if (!this.readOnly()) {
            this.form.controls.schedules.enable({ emitEvent: false });
          }
        } else {
          if (this.form.controls.collectiveSchedules.value) {
            this.onHorairesCollectifsChange(true);
          }
        }
        this.applyReadOnlyState(this.readOnly());
      });
      this.cdr.detectChanges();
      return;
    }
  }

  addSlot() {
    if (this.readOnly()) {
      return;
    }

    this.form.controls.schedules.controls.forEach((daySchedule) => {
      daySchedule.controls.slots.push(createSlotFormGroup({ startTime: '', endTime: '' }));
    });
  }

  removeLastSlots() {
    if (this.readOnly()) {
      return;
    }

    this.form.controls.schedules.controls.forEach((daySchedule) => {
      const slots = daySchedule.controls.slots;
      if (slots.length > 1) {
        slots.removeAt(slots.length - 1);
      }
    });
  }

  onHorairesCollectifsChange(checked: boolean) {
    if (this.readOnly()) {
      return;
    }

    this.slotsMatTooltip = null;
    if (checked) {
      const schedulesFA = new FormArray(
        (this.companySchedules || []).map(
          (schedule) =>
            new FormGroup({
              day: new FormControl<DayOfWeek>(schedule.day, { nonNullable: true }),
              slots: new FormArray(schedule.slots.map((slot: Slot) => createSlotFormGroup(slot))),
            }),
        ),
        { validators: [minTimeBetweenDay(11), maxTotalHours(35)] },
      );
      this.form.setControl('schedules', schedulesFA);
      schedulesFA.disable({ emitEvent: false });
      this.slotsMatTooltip =
        "Les horaires sont définis par l'entreprise et ne peuvent pas être modifiés ici.";
    } else {
      // Revenir à des horaires individuels (slots vides)
      this.slotsMatTooltip = null;
      const schedulesFA = new FormArray(
        this.daysOfWeek.map((day) => createScheduleFormGroupOfDay(day)),
        { validators: [minTimeBetweenDay(11), maxTotalHours(35)] },
      );
      this.form.setControl('schedules', schedulesFA);
      this.cdr.detectChanges();
      this.form.markAllAsTouched();
    }
    this.form.controls.schedules.updateValueAndValidity({ emitEvent: true });
  }

  get isValid(): boolean {
    if (this.validation()) {
      if (!this.form.controls.collectiveSchedules.value) {
        const allSlotsEmpty = this.form.controls.schedules.controls.every((daySchedule) =>
          daySchedule.controls.slots.controls.every(
            (slot) => !slot.controls.startTime.value && !slot.controls.endTime.value,
          ),
        );
        if (allSlotsEmpty) return false;
      }
    }
    return this.form.valid;
  }

  get isDirty(): boolean {
    if (!this.initialValues) return false;
    const current: SchedulesFormValue = {
      ...this.form.getRawValue(),
    } as SchedulesFormValue;
    const modifiedKeys = Object.keys(this.initialValues).filter((k) => {
      const key = k as keyof SchedulesFormValue;
      const a = current[key];
      const b = this.initialValues![key];
      if (
        this.initialValues![key] instanceof Array ||
        typeof this.initialValues![key] === 'object'
      ) {
        try {
          return JSON.stringify(a) !== JSON.stringify(b);
        } catch (e) {
          console.error('Error comparing schedules:', e);
          return true;
        }
      }
      return a !== b;
    });
    return modifiedKeys.length > 0;
  }

  setWeekSlots(ofDayIndex: number) {
    if (this.readOnly()) {
      return;
    }

    const slots = this.form.controls.schedules.at(ofDayIndex).controls.slots.value as Slot[];
    this.form.controls.schedules.controls.forEach((daySchedule, index) => {
      if (index !== ofDayIndex) {
        daySchedule.controls.slots.setValue(slots);
      }
    });
  }

  clearDaySlots(ofDay: DayOfWeek) {
    if (this.readOnly()) {
      return;
    }

    const ofDayIndex = this.daysOfWeek.indexOf(ofDay);
    this.form.controls.schedules.at(ofDayIndex).controls.slots.controls.forEach((slot) => {
      slot.reset({ startTime: null, endTime: null });
    });
  }

  resetWeekSlots() {
    if (this.readOnly()) {
      return;
    }

    this.form.controls.schedules.patchValue(this.initialValues?.schedules || []);
  }

  private applyReadOnlyState(readOnly: boolean): void {
    if (readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    if (!this.companyId || !this.companySchedules || this.companySchedules.length === 0) {
      this.form.controls.collectiveSchedules.disable({ emitEvent: false });
    }

    if (this.form.controls.collectiveSchedules.value) {
      this.form.controls.schedules.disable({ emitEvent: false });
    } else {
      this.form.controls.schedules.enable({ emitEvent: false });
    }
  }

  onPreviousContractSelected(schedulePart: SchedulePart) {
    this.form.patchValue(schedulePart);
    this.onHorairesCollectifsChange(schedulePart.collectiveSchedules || false);
  }
}
